/**
 * Quiet defaults and Inbox mode (M1.18.1, [D18](../../../Design-Decisions.md)).
 *
 * **Counts and badges are off for every new account.** A precise red badge on
 * first launch contradicts the product's positioning within five seconds of
 * opening it — the pitch is a reader that does not shout, and a number that
 * says 1,204 is shouting. Inbox mode turns exact counts on at feed, folder and
 * global level.
 *
 * It is a change of *default*, not of capability. The backend computes and
 * returns exact counts either way; this decides whether they are drawn. That
 * matters because it means switching modes needs no migration and no refetch,
 * and because an alpha tester who turns it on gets the real numbers
 * immediately rather than numbers that start from the moment they asked.
 *
 * ## Why it is asked once, up front
 *
 * D18's risk runs both ways: if Inbox mode is undiscoverable, Maya concludes
 * the product cannot count and leaves. A single onboarding question serves
 * both — the default stays quiet, and anybody who wants numbers is told in one
 * sentence that they exist.
 *
 * ## Stored per account, on the device
 *
 * Per account, because two people sharing a laptop should not share a display
 * mode. On the device rather than the server because it is a display
 * preference with no server behaviour behind it, and putting it in the profile
 * would mean a write on the API's critical path for something that is already
 * correct locally. The cost is that a second device asks again — which, for a
 * one-question onboarding, is closer to right than wrong.
 */

export interface Preferences {
  /** Exact counts and badges. Off until someone says otherwise (D18). */
  readonly inboxMode: boolean;
  /** Whether the onboarding question has been answered at all. */
  readonly onboarded: boolean;
  /** Folder ids the viewer has collapsed (M1.15.1). */
  readonly collapsedFolders: readonly string[];
}

export const DEFAULT_PREFERENCES: Preferences = {
  inboxMode: false,
  onboarded: false,
  collapsedFolders: [],
};

/** Keyed by account, so a shared device does not share a mode. */
const storageKey = (userKey: string): string => `feedreader.preferences.${userKey}`;

export interface PreferenceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function loadPreferences(
  userKey: string,
  storage: PreferenceStorage,
): Preferences {
  let raw: string | null = null;
  try {
    raw = storage.getItem(storageKey(userKey));
  } catch {
    /*
     * Storage can throw outright — Safari in private browsing, a blocked
     * third-party context. A reader whose preferences cannot be saved should
     * still get a reader, with the defaults.
     */
    return DEFAULT_PREFERENCES;
  }

  if (raw === null) {
    return DEFAULT_PREFERENCES;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      inboxMode: parsed.inboxMode === true,
      onboarded: parsed.onboarded === true,
      collapsedFolders: Array.isArray(parsed.collapsedFolders)
        ? parsed.collapsedFolders.filter((id): id is string => typeof id === 'string')
        : [],
    };
  } catch {
    // Corrupt or written by an older version: the defaults are safe, and the
    // quiet default is the safe direction to fail in.
    return DEFAULT_PREFERENCES;
  }
}

export function savePreferences(
  userKey: string,
  preferences: Preferences,
  storage: PreferenceStorage,
): void {
  try {
    storage.setItem(storageKey(userKey), JSON.stringify(preferences));
  } catch {
    // Ignored, as above.
  }
}

export const toggleCollapsed = (
  preferences: Preferences,
  folderId: string,
): Preferences => ({
  ...preferences,
  collapsedFolders: preferences.collapsedFolders.includes(folderId)
    ? preferences.collapsedFolders.filter((id) => id !== folderId)
    : [...preferences.collapsedFolders, folderId],
});
